import { EditOutlined, ListAltTwoTone } from '@mui/icons-material';
import { Box, IconButton, Stack } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useOperatorsMap, useStockLocationsMap } from '../../../admin/hooks';
import {
    formatDateTimeString,
    IParams,
    IProduct,
    mapDateStringToApiDate,
    MeasurementUnit,
    PermissionKeys,
    SortOrder,
    Table,
    useHasPermission,
    useOrganisationsMap,
    useQueryParams,
    Widget,
} from '../../../shared';
import { StockMovementType } from '../../enums';
import { useStockMovementsList } from '../../hooks';
import { StockMovementsFilter } from '../stock-movements-filter/stock-movements-filter.component';

export const BatchStockMovements: FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<keyof IParams>() as IParams;
    const navigate = useNavigate();

    const { hasPermission } = useHasPermission();
    const hasWritePermission = hasPermission(PermissionKeys.BATCHES_WRITE);
    const [filter, setFilter] = useState<Record<string, any>>({});

    const organisations = useOrganisationsMap();
    const operators = useOperatorsMap();
    const stockLocations = useStockLocationsMap();

    const { page, pageSize, sort, setPage, setPageSize, setSort } = useQueryParams({
        defaultSort: [{ field: 'createdDate', sort: 'desc' }],
    });

    const { data: stockMovements, isPending } = useStockMovementsList(
        {
            page: page,
            pageSize: pageSize,
            sortBy: sort[0].field,
            sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
            batch: id,
            date: mapDateStringToApiDate(filter.date) || undefined,
            type: (filter.type as StockMovementType) || undefined,
            product: filter.product || undefined,
            operator: filter.operator || undefined,
            stockLocation: filter.stockLocation || undefined,
            organisation: filter.organisation || undefined,
            productCode: filter.productCode || undefined,
            includeRelations: false,
        },
        { refetchInterval: 10000 },
    );

    const columns: GridColDef[] = [
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            valueFormatter: (value: string) => formatDateTimeString(value),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'type',
            headerName: t('type'),
            valueFormatter: (value: string) => t(value),
            flex: 1,
            minWidth: 100,
        },
        {
            field: 'operatorId',
            headerName: t('operator'),
            valueFormatter: (value: string) => operators[value],
            flex: 1,
            minWidth: 150,
            sortable: false,
        },
        {
            field: 'stockLocationId',
            headerName: t('stockLocation'),
            valueFormatter: (value: string) => stockLocations[value],
            flex: 1,
            minWidth: 150,
            sortable: false,
        },
        {
            field: 'organisationId',
            headerName: t('organisation'),
            valueFormatter: (value: string) => organisations[value],
            flex: 1,
            minWidth: 150,
            sortable: false,
        },
        {
            field: 'product',
            headerName: t('product'),
            valueFormatter: (value: IProduct) => value?.name,
            flex: 2,
            minWidth: 200,
            sortable: false,
        },
        {
            field: 'amount',
            headerName: t('amount'),
            minWidth: 150,
            flex: 1,
            renderCell: ({ row }: GridCellParams) => (
                <>
                    {row.amount}{' '}
                    {!row.product ? '' : row.product?.measurementUnit === MeasurementUnit.GRAM ? t('gram') : t('PIECE')}
                </>
            ),
            sortable: false,
        },
        {
            field: 'totalWeight',
            headerName: t('totalWeight'),
            minWidth: 150,
            flex: 1,
            renderCell: ({ row }: GridCellParams) => <>{row.product && row.amount * (row.product.weight || 1)}</>,
            sortable: false,
        },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 65,
            align: 'right',
            sortable: false,
            hideable: false,
            renderCell: ({ row }: GridCellParams) => (
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        hasWritePermission &&
                            navigate(`/stock/movements/${row.id}/edit`, { state: `/stock/batches/${id}/detail` });
                    }}
                >
                    <EditOutlined />
                </IconButton>
            ),
        },
    ];

    return (
        <Widget
            title={
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ minWidth: 'fit-content' }}>{t('stockMovements')}</Box>
                    <StockMovementsFilter filter={filter} onChange={setFilter} showSupplier={false} />
                </Stack>
            }
            icon={<ListAltTwoTone color="primary" />}
            hasTable
        >
            <Stack spacing={2}>
                <Table
                    rows={stockMovements?.data || []}
                    columns={columns}
                    rowCount={stockMovements?.pagination?.size || 0}
                    page={page}
                    pageSize={pageSize}
                    setPage={setPage}
                    setPageSize={setPageSize}
                    loading={isPending}
                    sortModel={sort}
                    onSortModelChange={setSort}
                    onRowClick={(row) =>
                        hasWritePermission &&
                        navigate(`/stock/movements/${row.id}/edit`, { state: `/stock/batches/${id}/detail` })
                    }
                    prefix="batch-stock-movements"
                    disableColumnMenu={false}
                />
            </Stack>
        </Widget>
    );
};
